/**
 * Root module — the onboarding package's root provider.
 *
 * `OnboardingRoot` takes **configuration and ports**, and turns them into a mounted flow:
 * it publishes the leaf env values, publishes the analytics port it was handed, and points
 * our own Transifex instance at our token — all during render, before children can read any
 * of it.
 *
 * **Ports, not credentials, for vendors somebody else may already own.** A host passes its
 * `analytics` (and later its `apiClient`) and onboarding telemetry lands on the host's
 * identity, session and Datadog service instead of splitting one user across two clients.
 * Standalone, `createVidaVendors` from
 * `@vidahealth/onboarding/vidaVendors` supplies exactly the same shape, built from
 * `EXPO_PUBLIC_*`. There is no `embedded` flag: ownership is what you pass.
 *
 * Transifex is the exception, and it is why this is decided per vendor rather than per
 * mount: our strings live in a different Transifex project from login's, so a host's `tx`
 * could only render our source English. We own that instance always; a host supplies
 * `locale` and nothing more. See `docs/vendor-ownership-proposal.md`.
 *
 * **Scope stops at configuration.** `AppThemeProvider`, `RulesEngineProvider` and
 * `OnboardingStepsProvider` stay in `app/_layout.tsx` until a consumer mounts the flow
 * itself, which is also when a React context earns its place (the host's callbacks and
 * mount path, as in login's `contexts/`).
 *
 * **This surface exports one thing, and does not re-export other modules' types.** The prop
 * types a caller needs come from the module that owns them — `AnalyticsPort` and
 * `OnboardingVendors` from `@/modules/analytics`, `OnboardingConfig` from
 * `@/modules/runtimeConfig`, `OnboardingApiClient` from `@/modules/api`. Re-exporting them
 * here to save a caller an import line was a barrel-of-convenience reflex and it was wrong:
 * it gives one type two importable paths with nothing marking either canonical, and it folds
 * `analytics`'s public surface into this one, so widening `AnalyticsPort` would silently be a
 * breaking change to `root`'s API too. That ambiguity would harden the moment a second
 * subpath is published and a consumer could reach the same type two ways.
 */
export { OnboardingRoot } from './contexts/OnboardingRoot';
